import { feature } from "topojson-client";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import { VOIVODESHIP_PL_NAMES } from "../i18n/voivodeships.pl";
import { MapData, Region, RegionProps } from "../types";

interface LoadOptions {
  useSimplified?: boolean;
  useTopojson?: boolean;
}

const DEFAULT_OPTIONS: Required<LoadOptions> = {
  useSimplified: true,
  useTopojson: true,
};

const MAP_PATHS = {
  topoSimplified: `${process.env.PUBLIC_URL}/maps/poland/geoBoundaries-POL-ADM1_simplified.topojson`,
  geoFull: `${process.env.PUBLIC_URL}/maps/poland/geoBoundaries-POL-ADM1.geojson`,
};

function withPolishProps(props: RegionProps): RegionProps {
  const plName = VOIVODESHIP_PL_NAMES[props.shapeISO] ?? props.shapeName;
  return { ...props, plName };
}

export async function loadPolandAdm1(
  options: LoadOptions = {},
): Promise<MapData> {
  const settings = { ...DEFAULT_OPTIONS, ...options };
  if (!settings.useSimplified) {
    settings.useTopojson = false;
  }
  let featureCollection: FeatureCollection<Geometry, RegionProps> | null = null;

  if (settings.useTopojson) {
    try {
      const topoResponse = await fetch(MAP_PATHS.topoSimplified);
      if (!topoResponse.ok) {
        throw new Error("Failed to load topojson");
      }
      const topo = await topoResponse.json();
      const objectKey = Object.keys(topo.objects)[0];
      const geo = feature(topo, topo.objects[objectKey]) as unknown;
      if (!geo || typeof geo !== "object" || !("features" in geo)) {
        throw new Error("TopoJSON did not produce a FeatureCollection");
      }
      featureCollection = geo as FeatureCollection<Geometry, RegionProps>;
    } catch (error) {
      featureCollection = null;
    }
  }

  if (!featureCollection) {
    const geoResponse = await fetch(MAP_PATHS.geoFull);
    if (!geoResponse.ok) {
      throw new Error("Failed to load geojson");
    }
    featureCollection = (await geoResponse.json()) as FeatureCollection<
      Geometry,
      RegionProps
    >;
  }

  const regions: Region[] = featureCollection.features.map((rawFeature) => {
    const props = withPolishProps(rawFeature.properties as RegionProps);
    const feature = {
      ...rawFeature,
      properties: props,
    } as Feature<Geometry, RegionProps>;

    return {
      id: props.shapeISO,
      props,
      feature,
      path: new Path2D(),
      bbox: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
      centroid: [0, 0],
    };
  });

  const enrichedFeatureCollection: FeatureCollection<Geometry, RegionProps> = {
    ...featureCollection,
    features: regions.map((region) => region.feature),
  };

  return { featureCollection: enrichedFeatureCollection, regions };
}
